import { type RefObject, useCallback, useEffect, useState } from "react";
import type { FlatList } from "react-native";

import { events } from "@constants/tracking";
import { useSessionItemsNavLayout } from "@features/session/contexts/SessionItemsNavLayoutContext";
import useAnalytics from "@hooks/useAnalytics";
import { useAppStore } from "@hooks/useAppStore";
import { useGlobalStore } from "@hooks/useGlobalStore";
import type { Confidence } from "@memorang/types";
import AboutQuestionDialog from "@memorang/ui/components/AboutQuestionDialog";
import Box from "@memorang/ui/components/Box";
import { ConfirmDialog } from "@memorang/ui/components/ConfirmDialog";
import { SessionItemsNavPane } from "@memorang/ui/components/SessionItemsNavPane";
import FinalizingResultsLoader from "@memorang/ui/components/StudySession/FinalizingResultsLoader";
import SessionEndDialog from "@memorang/ui/components/StudySession/SessionEndDialog";
import StudySessionBottomAppBar from "@memorang/ui/components/StudySession/StudySessionBottomAppbar";
import PteSummativeBottomAppbar from "@memorang/ui/components/StudySession/StudySessionBottomAppbar/SummativeBottomAppbar/Pte";
import TutorialDialog from "@memorang/ui/components/StudySession/TutorialDialog";
import { useDevice } from "@memorang/ui/useDevice";
import { router, useLocalSearchParams } from "expo-router";
import { useTranslation } from "react-i18next";
import DialogSession from "../../components/SessionDialog";
import { tutorialDialogMetadata } from "../../components/dialogs/constants";
import type { DialogType } from "../../constants/dialog-configs";
import { isPteAssessorAgentItem } from "../../constants/pte/assessor";
import { isPteFeedbackAgentItem } from "../../constants/pte/feedback";
import { isIntroScreen } from "../../helpers/intro-screens";
import useExitReview from "../../hooks/useExitReview";
import { usePteAssessorAgent } from "../../hooks/usePteAssessorAgent";
import { usePteFeedbackAgent } from "../../hooks/usePteFeedbackAgent";
import BreakDialogContainer from "../BreakDialogContainer";
import ExpiredBreakDialogContainer from "../BreakDialogContainer/ExpiredBreakDialogContainer";
import {
	buildButtonConfig,
	buildQuestionsConfig,
	getNextButtonState,
} from "./button-config";
import useAutoSkipOnTimeout from "./useAutoSkipOnTimeout";
import useMeetsMinWords from "./useMeetsMinWords";
import useQuestionTipsDialog from "./useQuestionTipsDialog";
import useSessionAnswerHandlers from "./useSessionAnswerHandlers";
import useSessionBottomBarState from "./useSessionBottomBarState";
import useSessionNavigationHandlers from "./useSessionNavigationHandlers";

interface Props {
	flashlistRef?: RefObject<FlatList | null>;
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: orchestration container
const StudySessionBottombarContainer = ({ flashlistRef }: Props) => {
	const { index } = useLocalSearchParams<{ index: string }>();
	const currentItemIndex = index ? Number.parseInt(index) - 1 : 0;

	const [tempSelectedConfidence, setTempSelectedConfidence] =
		useState<Confidence | null>(null);
	const [helpDialogVisible, setHelpDialogVisible] = useState(false);
	const [startTime, setStartTime] = useState<Date>(new Date());
	const [showEndSessionDialog, setShowEndSessionDialog] = useState(false);
	const [showSessionDialogData, setShowSessionDialogData] = useState<{
		show: boolean;
		type?: DialogType;
	}>({ show: false });

	const { isMobile, isLargeWindowSize } = useDevice();
	const sessionItemsNav = useSessionItemsNavLayout();
	const { t } = useTranslation();
	const { explicitCallTrackCustomEvent } = useAnalytics();
	const testDriver = useAppStore((s) => s.sessionView?.testDriver);
	const appFont = useAppStore((s) => s.app.font) || "opensans";

	const state = useSessionBottomBarState();

	const handleExitReview = useExitReview();

	const setHasShownConfidenceOnboardingDialog = useGlobalStore(
		(s) => s.setHasShownConfidenceOnboardingDialog,
	);

	const answerHandlers = useSessionAnswerHandlers({
		startTime,
		currentItemIndex,
		showButtonNavSkip: state.showButtonNavSkip,
		slideshowDurationInSeconds: state.slideshowDurationInSeconds,
		onTempConfidence: setTempSelectedConfidence,
	});

	const { generate: generatePteAssessment, phase: pteAssessorPhase } =
		usePteAssessorAgent({
			onComplete: () => answerHandlers.handleTrackAnswerEventAi(50),
		});
	const isPteAssessor = isPteAssessorAgentItem(
		state.currentItem.metadata?.type,
	);
	const meetsMinWords = useMeetsMinWords(state.currentItem);
	const showPteAssessorButton =
		isPteAssessor && state.showSubmitAnswerAIFeedbackButton && meetsMinWords;
	const disablePteAssessorButton =
		pteAssessorPhase !== "idle" && pteAssessorPhase !== "error";

	const handlePteAssessorSubmit = useCallback(() => {
		answerHandlers.onConfidenceProvided(50);
		generatePteAssessment().catch(() => {
			// Stream/upload errors are reported to Sentry in usePteAssessorAgent's onError.
		});
	}, [answerHandlers, generatePteAssessment]);

	const { generate: generateFeedback, phase: feedbackAgentPhase } =
		usePteFeedbackAgent();
	const isPteFeedback = isPteFeedbackAgentItem(
		state.currentItem.metadata?.type,
	);
	const showFeedbackAgentButton =
		isPteFeedback &&
		meetsMinWords &&
		(state.showSubmitAnswerAIFeedbackButton ||
			Boolean(state.showConfidenceSubmitButtons));
	const disableFeedbackAgentButton =
		feedbackAgentPhase !== "idle" && feedbackAgentPhase !== "error";

	const handleFeedbackAgentSubmit = useCallback(() => {
		answerHandlers.onConfidenceProvided(50);
		generateFeedback();
	}, [answerHandlers, generateFeedback]);

	const agentButton = showPteAssessorButton
		? {
				onPress: handlePteAssessorSubmit,
				disabled: disablePteAssessorButton,
				label: t("common.submitResponse"),
			}
		: showFeedbackAgentButton
			? {
					onPress: handleFeedbackAgentSubmit,
					disabled: disableFeedbackAgentButton,
					label: t("common.submitResponse"),
				}
			: undefined;

	const toggleShowEndSessionDialog = () =>
		setShowEndSessionDialog((prev) => !prev);

	const handleCloseDialog = useCallback(
		() => setShowSessionDialogData({ show: false }),
		[],
	);

	const { handleNext, handlePrevious, moveToNextBlock, finalizingProgress } =
		useSessionNavigationHandlers({
			handleUpdateAnswers: answerHandlers.handleUpdateAnswers,
			toggleShowEndSessionDialog,
			setShowSessionDialogData,
			showButtonNavSkip: state.showButtonNavSkip,
			flashlistRef,
		});

	const {
		currentResponseTimeLimit,
		handleNextAfterResponseCompletion,
		advanceAfterTimeExpired,
	} = useAutoSkipOnTimeout({
		handleNext,
		onTimeExpired: () =>
			setShowSessionDialogData({ show: true, type: "itemTimeExpired" }),
	});

	const handleClickActionButton = useCallback(
		(type: string) => {
			handleCloseDialog();
			if (type === "cancel") {
				return;
			}
			if (showSessionDialogData.type === "itemTimeExpired") {
				advanceAfterTimeExpired();
				return;
			}
			moveToNextBlock();
		},
		[
			handleCloseDialog,
			moveToNextBlock,
			advanceAfterTimeExpired,
			showSessionDialogData.type,
		],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: reset start time on item change
	useEffect(() => {
		setStartTime(new Date());
	}, [state.currentItemId]);

	useEffect(() => {
		if (currentItemIndex === 0) {
			const sectionLength =
				state.currentBlock?.filter((item) => !isIntroScreen(item)).length ||
				state.currentBlock?.length ||
				state.numGoal!;
			const initProgress = 1 / sectionLength;
			state.updateProgress(initProgress);
		}
	}, [
		currentItemIndex,
		state.currentBlock,
		state.numGoal,
		state.updateProgress,
	]);

	const handleNavigation = (type: "positive" | "negative") => {
		if (type === "positive") {
			router.replace("/home");
		} else {
			toggleShowEndSessionDialog();
		}
	};

	const showConfidenceOnboardingDialog = tempSelectedConfidence != null;

	const handleToggleHelpDialog = () => {
		if (isMobile) {
			router.push({
				pathname: "/info",
				params: {
					title: state.confirmDialogConfig.title,
					body: state.confirmDialogConfig.body,
				},
			});
		} else {
			setHelpDialogVisible((prev) => !prev);
		}
	};

	const buttonConfig = buildButtonConfig({
		streaming: state.streaming,
		nextButtonText: state.nextButtonText,
		prevButtonText: state.prevButtonText,
		disableNextButton: state.disableNextButton,
		nextButtonTooltip: state.nextButtonTooltip,
		isRecordingActive: state.isRecordingActive,
		recordingActiveTooltip: state.recordingActiveTooltip,
		inReviewMode: state.inReviewMode,
		t,
	});

	const questionsConfig = state.allowBackNavigation
		? buildQuestionsConfig(sessionItemsNav, state.isSummativeTest, t)
		: undefined;

	const handleTrackConfidenceClick = (confidence: Confidence) => {
		explicitCallTrackCustomEvent({
			eventName: events.sessionItemConfidenceClicked,
			confidence,
			itemId: state.currentItemId,
			sessionId: state.id,
		});
	};

	const isPteDriver = testDriver === "pte" && state.isSummativeTest;
	// The TOEIC driver surfaces exit from the app bar menu instead.
	const isToeicDriver = testDriver === "toeic" && state.isSummativeTest;

	const questionTips = useQuestionTipsDialog({
		currentItem: state.currentItem,
		isSummativeTest: state.isSummativeTest,
		inReviewMode: state.inReviewMode,
		sessionId: state.id,
	});
	const { aboutInfo } = questionTips;

	return (
		<Box maxWidth={"100%"} alignSelf="center" width="100%">
			{isPteDriver ? (
				<PteSummativeBottomAppbar
					handleNext={handleNextAfterResponseCompletion}
					inReviewMode={state.inReviewMode}
					nextDisabled={state.disableNextButton}
					nextButtonTooltip={state.nextButtonTooltip?.next}
					nextButtonState={getNextButtonState({
						showButtonNavFinish: state.showButtonNavFinish,
						showButtonNavSkip: state.showButtonNavSkip,
						streaming: state.streaming,
					})}
					showPreviousButton={state.inReviewMode && state.showPreviousButton}
					handlePrevious={handlePrevious}
					exitConfig={
						state.inReviewMode
							? {
									onPress: handleExitReview,
									label: t("session.navigation.exit"),
								}
							: isIntroScreen(state.currentItem)
								? {
										onPress: () => {
											answerHandlers.handleUpdateAnswers(50);
											router.replace("/home");
										},
										label: t("session.navigation.saveAndExit"),
									}
								: undefined
					}
				/>
			) : (
				<StudySessionBottomAppBar
					handleNext={handleNextAfterResponseCompletion}
					buttonConfig={buttonConfig}
					nextButtonState={getNextButtonState({
						showButtonNavFinish: state.showButtonNavFinish,
						showButtonNavSkip: state.showButtonNavSkip,
						streaming: state.streaming,
					})}
					handlePrevious={handlePrevious}
					handleTrackConfidenceClick={handleTrackConfidenceClick}
					showSubmitAnswerAIFeedbackButton={
						!(isPteAssessor || isPteFeedback) &&
						state.showSubmitAnswerAIFeedbackButton
					}
					isSummativeTest={state.isSummativeTest}
					showPreviousButton={state.showPreviousButton}
					inReviewMode={state.inReviewMode}
					handleConfidenceProvided={answerHandlers.onConfidenceProvided}
					showConfidenceSubmitButtons={
						Boolean(state.showConfidenceSubmitButtons) && !isPteFeedback
					}
					handleAiFeedbackProvided={answerHandlers.handleTrackAnswerEventAi}
					handleGenerateFeedback={answerHandlers.generateExplanation}
					submitAnswerDynamicCta={t("common.submitResponse")}
					toggleHelpDialog={handleToggleHelpDialog}
					questionsConfig={questionsConfig}
					aboutConfig={questionTips.aboutConfig}
					exitConfig={
						state.inReviewMode && !isToeicDriver
							? {
									onPress: handleExitReview,
									label: t("session.navigation.exit"),
								}
							: undefined
					}
					agentButton={agentButton}
				/>
			)}

			{sessionItemsNav &&
				state.isSummativeTest &&
				!isLargeWindowSize &&
				sessionItemsNav.showSessionItemsNav && (
					<SessionItemsNavPane
						presentation={isMobile ? "bottom-sheet" : "side-sheet"}
						visible={sessionItemsNav.showSessionItemsNav}
						onClose={sessionItemsNav.handleCloseSessionItemsNav}
						items={sessionItemsNav.sessionItemsNavItems}
						activeItemIndex={currentItemIndex}
						filters={sessionItemsNav.sessionItemsNavFilters}
						selectedFilters={sessionItemsNav.sessionItemsNavSelectedFilters}
						onSelectedFiltersChange={
							sessionItemsNav.setSessionItemsNavSelectedFilters
						}
						onItemPress={sessionItemsNav.handleSessionItemsNavPress}
					/>
				)}

			{finalizingProgress && (
				<FinalizingResultsLoader
					visible
					completed={finalizingProgress.completed}
					total={finalizingProgress.total}
				/>
			)}

			{showSessionDialogData.show && (
				<DialogSession
					type={showSessionDialogData.type}
					handleClickButton={handleClickActionButton}
					handleClose={handleCloseDialog}
					itemTimeLimitSeconds={currentResponseTimeLimit}
					isLastQuestion={state.isLastBlock && state.isLastItemInTheBlock}
					open={showSessionDialogData.show}
					currentSection={
						state.numTotalParts > 1
							? state.sectionIndexWithinPart + 1
							: state.currentBlockIndex + 1
					}
					numTotalSections={
						state.numTotalParts > 1
							? state.currentPart?.blockIndices.length
							: state.numSections
					}
					currentPart={state.currentPartIndex + 1}
					numTotalParts={state.numTotalParts}
					nextPartName={
						state.parts[state.currentPartIndex + 1]?.partName || undefined
					}
				/>
			)}

			{showConfidenceOnboardingDialog && (
				<TutorialDialog
					open={showConfidenceOnboardingDialog}
					handleClose={() => {
						if (tempSelectedConfidence) {
							answerHandlers.handleUpdateAnswers(tempSelectedConfidence);
						}
						setTempSelectedConfidence(null);
						setHasShownConfidenceOnboardingDialog(true);
					}}
					{...tutorialDialogMetadata.confidence}
					appFont={appFont}
				/>
			)}

			{showEndSessionDialog && (
				<SessionEndDialog
					visible={showEndSessionDialog}
					onDismiss={toggleShowEndSessionDialog}
					handleAction={handleNavigation}
					hasAnsweredAtLeastOneItem={false}
				/>
			)}

			{helpDialogVisible && (
				<ConfirmDialog
					visible={helpDialogVisible}
					title={state.confirmDialogConfig.title}
					icon={state.confirmDialogConfig.icon}
					body={state.confirmDialogConfig.body}
					dismissCallback={() => setHelpDialogVisible(false)}
					confirmCallback={() => setHelpDialogVisible(false)}
					confirmCta="Close"
				/>
			)}

			{state.showBreakDialog &&
				state.serverExamBreak?.allotedTime &&
				state.sectionId && (
					<BreakDialogContainer
						showBreakDialog={state.showBreakDialog}
						setShowBreakDialog={state.toggleShowBreakDialog}
						breakTime={state.serverExamBreak.allotedTime}
						sectionId={state.sectionId}
					/>
				)}

			{state.serverExamBreak?.allotedTime && state.sectionId && (
				<ExpiredBreakDialogContainer
					setShowBreakDialog={state.toggleShowBreakDialog}
					breakTime={state.serverExamBreak.allotedTime}
					sectionId={state.sectionId}
					sessionId={state.id}
				/>
			)}

			{aboutInfo && (
				<AboutQuestionDialog
					visible={questionTips.visible}
					onClose={questionTips.handleClose}
					hideFutureConfig={questionTips.hideFutureConfig}
					title={aboutInfo.questionTitle}
					levels={aboutInfo.cefrBands?.map((band) => ({
						value: band,
						label: band,
					}))}
					selectedLevel={aboutInfo.activeCefr ?? undefined}
					strategies={aboutInfo.tips}
					tip={aboutInfo.insight}
					primaryActionLabel={t("common.gotIt")}
				/>
			)}
		</Box>
	);
};

export default StudySessionBottombarContainer;
